import { useCallback, useEffect, useState } from 'react';
import api from './api';

/*
 * AI credits (routes/ai_credits.py, migration 0108). Public guests and
 * external researchers are metered: a monthly free allowance, then a purchased
 * balance. Anyone holding another role is not, and sees nothing of this.
 *
 * Every place that shows what is left (the badge, the AI panel) reads one
 * state: GET /ai-credits/me when it mounts, then whatever an answer brings --
 * POST /ai/identify's `credits`, a 402's -- announced to the others on the
 * window, so the badge counts down as the panel spends.
 */

/** The metered roles: routes/ai_credits.py METERED. */
export const METERED_ROLES = Object.freeze(['public', 'external_researcher']);

/** Whether this account is metered: every role it holds is an outsider's. */
export const isMetered = user => {
  const roles = user?.roles || [];
  return roles.length > 0 && roles.every(r => METERED_ROLES.includes(r));
};

/** Free credits left this month plus purchased ones. */
export const creditsLeft = credits => (credits?.free_remaining || 0) + (credits?.purchased_token_balance || 0);

const EVENT = 'pitriot:ai-credits';

/** Tell every credits display what the server last said. */
export function announceCredits(credits) {
  if (credits && credits.metered) window.dispatchEvent(new CustomEvent(EVENT, { detail: credits }));
}

/** {metered, credits, reload}: credits is null until known, and stays null
 *  for an account that is not metered. */
export function useAiCredits(user) {
  const metered = isMetered(user);
  const [credits, setCredits] = useState(null);
  const reload = useCallback(() => Promise.resolve()
    .then(() => api.get('/ai-credits/me'))
    .then(res => { if (res?.data?.metered) setCredits(res.data); })
    .catch(() => {}), []);

  useEffect(() => {
    if (!metered) {
      setCredits(null);
      return undefined;
    }
    reload();
    const heard = event => setCredits(event.detail);
    window.addEventListener(EVENT, heard);
    return () => window.removeEventListener(EVENT, heard);
  }, [metered, reload]);

  return { metered, credits, reload };
}
